import * as fe from "@runn/filter-engine"

import { ReportFiltersInitialState } from "../reports_v2/hooks/useReportFilters"
import { Actual, Assignment, Contract } from "../reports_v2/types"

const isWithinContractPeriod = (date: string, contract: any) => {
  const dateNum = Number(date)
  return (
    dateNum >= Number(contract.start_date) &&
    (!contract.end_date || dateNum <= Number(contract.end_date))
  )
}

export const filterPeopleByContracts = <
  TPerson extends {
    contracts: ReadonlyArray<Contract>
    assignments: ReadonlyArray<Pick<Assignment, "start_date" | "end_date">>
    actuals?: ReadonlyArray<Actual>
  },
>(
  people: ReadonlyArray<TPerson>,
  filterSet: ReportFiltersInitialState["filterSet"],
): ReadonlyArray<TPerson> => {
  const filters = filterSet?.filters ? fe.filters.flat(filterSet.filters) : []
  const roleFilter = filters.find((f) => f.type === "person_role_id")
  const employmentTypeFilter = filters.find(
    (f) => f.type === "person_employment_type",
  )
  const roleIds = roleFilter?.options?.list ?? null
  const employmentTypes = employmentTypeFilter?.options?.list ?? null

  if (!roleIds && !employmentTypes) return people

  const roleIdSet = roleIds ? new Set(roleIds) : null
  const employmentTypeSet = employmentTypes ? new Set(employmentTypes) : null

  return people.map((person) => {
    // Find contracts matching the filtered roles and/or employment types
    const matchingContracts = person.contracts.filter((contract) => {
      const matchesRole = !roleIdSet || roleIdSet.has(contract.role.id)
      const matchesEmploymentType =
        !employmentTypeSet || employmentTypeSet.has(contract.employment_type)
      return matchesRole && matchesEmploymentType
    })

    if (matchingContracts.length === 0) {
      return {
        ...person,
        assignments: [],
        actuals: [],
      }
    }

    // Filter assignments and actuals
    const filteredAssignments = person.assignments.filter((assignment) =>
      matchingContracts.some(
        (contract) =>
          isWithinContractPeriod(assignment.start_date, contract) ||
          isWithinContractPeriod(assignment.end_date, contract),
      ),
    )

    const filteredActuals =
      person.actuals?.filter((actual) =>
        matchingContracts.some((contract) =>
          isWithinContractPeriod(actual.date, contract),
        ),
      ) ?? []

    return {
      ...person,
      contracts: matchingContracts,
      assignments: filteredAssignments,
      actuals: filteredActuals,
    }
  })
}
